const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'https://api.gogosaporu.com';

// Función para hacer peticiones HTTP

// Refresca el token usando el refreshToken guardado
const refreshAuthToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No hay refreshToken disponible');
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken })
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'No se pudo refrescar el token');
  }
  const data = await response.json();
  if (data.token) {
    localStorage.setItem('authToken', data.token);
    return data.token;
  } else {
    throw new Error('Respuesta inválida al refrescar token');
  }
};

// apiRequest con lógica de refresh automático
const apiRequest = async (url, options = {}, retry = true) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (response.status === 401 || response.status === 403) {
      // Intentar refrescar token sólo una vez
      if (retry && localStorage.getItem('refreshToken')) {
        try {
          const newToken = await refreshAuthToken();
          // Reintentar petición con nuevo token
          const newHeaders = { ...options.headers, Authorization: `Bearer ${newToken}` };
          return await apiRequest(url, { ...options, headers: newHeaders }, false);
        } catch (refreshError) {
          // Si falla el refresh, limpiar tokens y lanzar error
          throw new Error('Sesión expirada. Por favor inicia sesión nuevamente.');
        }
      }
      // Si no hay refresh o ya reintentó, lanzar error original
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP Error: ${response.status}`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP Error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Imposible acceder al servidor');
    }
    throw error;
  }
};

// Función auxiliar para truncar el User-Agent
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.length > 90) {
    return userAgent.substring(0, 90) + '...';
  }
  return userAgent;
};

// Servicios de autenticación
export const authService = {
  async login(nickname, password) {
    return await apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        nickname,
        password,
        dispositivo: getDeviceInfo()
        // IP será extraída automáticamente por el backend
      }),
    });
  },

  async loginWithEmail(email, password) {
    return await apiRequest('/auth/login-email', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        dispositivo: getDeviceInfo()
        // IP será extraída automáticamente por el backend
      }),
    });
  },

  // Función que detecta automáticamente si es email o nickname
  async smartLogin(usernameOrEmail, password) {
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(usernameOrEmail);
    
    if (isEmail) {
      return await this.loginWithEmail(usernameOrEmail, password);
    } else {
      return await this.login(usernameOrEmail, password);
    }
  },

  async register(registroData) {
    return await apiRequest('/usuarios/registro', {
      method: 'POST',
      body: JSON.stringify(registroData),
    });
  },

  async logout(refreshToken) {
    return await apiRequest('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({
        refreshToken
      }),
    });
  }
};

// Servicios de usuario
export const userService = {
  async getUserProfile(nickname, token) {
    return await apiRequest(`/usuarios/${nickname}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  async updateProfile(nickname, profileData, token) {
    return await apiRequest(`/usuarios/${nickname}/perfil`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData),
    });
  },

  async deleteAccount(nickname, deleteData, token) {
    return await apiRequest(`/usuarios/${nickname}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(deleteData),
    });
  }
};

// Servicios de posts
export const postService = {
  // --- Like/Dislike ---
  async likePost(postId, userId, token) {
    return await apiRequest(`/likes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ postId, userId })
    });
  },

  async dislikePost(postId, userId, token) {
    return await apiRequest(`/dislikes`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ postId, userId })
    });
  },

  // Nuevo: obtener likes/dislikes públicos
  async getLikeDislikeStatus(postId) {
    // Devuelve la lista de likes y dislikes del post (público)
    const likes = await apiRequest(`/posts/${postId}/likes`, { method: 'GET' });
    const dislikes = await apiRequest(`/posts/${postId}/dislikes`, { method: 'GET' });
    // El frontend puede decidir cómo mostrar si el usuario actual ha dado like/dislike
    return { likes, dislikes };
  },

  async getLikeDislikeCounts(postId) {
    // Devuelve el conteo de likes y dislikes del post (público)
    const likes = await apiRequest(`/posts/${postId}/likes/count`, { method: 'GET' });
    const dislikes = await apiRequest(`/posts/${postId}/dislikes/count`, { method: 'GET' });
    return { likes, dislikes };
  },
  async getAllPosts(token) {
    return await apiRequest('/posts', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  async getUserPosts(nickname, token) {
    return await apiRequest(`/posts/usuario/${nickname}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  async getPostComments(postId, token) {
    return await apiRequest(`/posts/${postId}/comentarios`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  },

  async createPost(postData, token) {
    // Para FormData, no establecer Content-Type (lo hace automáticamente el navegador)
    const headers = {
      'Authorization': `Bearer ${token}`
    };
    
    return await fetch(`${API_BASE_URL}/posts/multimedia`, {
      method: 'POST',
      headers,
      body: postData, // FormData
    }).then(async response => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP Error: ${response.status}`);
      }
      return response.json();
    }).catch(error => {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Imposible acceder al servidor');
      }
      throw error;
    });
  },

  async updatePost(postId, postData, token) {
    const headers = {
      'Authorization': `Bearer ${token}`
    };
    
    return await fetch(`${API_BASE_URL}/posts/${postId}`, {
      method: 'PUT',
      headers,
      body: postData, // FormData
    }).then(async response => {
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP Error: ${response.status}`);
      }
      return response.json();
    }).catch(error => {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Imposible acceder al servidor');
      }
      throw error;
    });
  },

  async deletePost(postId, usuarioNickname, token) {
    return await apiRequest(`/posts/${postId}?usuarioNickname=${usuarioNickname}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }
};

export default {
  authService,
  userService,
  postService
};
